import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import type { FileConversionStatus } from '@app/core/services/file-server.types';

@Component({
    selector: 'tt-document-viewer',
    templateUrl: './document-viewer.component.html',
    styleUrls: ['./document-viewer.component.css'],
    standalone: false,
})
export class DocumentViewerComponent implements OnChanges {
    @Input()
    public file?: DocumentViewerFile;

    @Input()
    public canConvert = true;

    @Output()
    public officeConversionCompleted = new EventEmitter<FileConversionStatus>();

    public type: DocumentViewerType = 'none';

    public sourceUrl = '';

    public frameUrl?: SafeResourceUrl;

    public imageMissing = false;

    public emailKeyno?: string | number | null;

    constructor(private sanitizer: DomSanitizer) {}

    public ngOnChanges(changes: SimpleChanges): void {
        if (!changes['file']) return;

        this.type = this.getDocumentType(this.file);
        this.sourceUrl = this.file?.imageLink?.trim() ?? '';
        this.frameUrl = this.type === 'pdf' && this.sourceUrl ? this.sanitizer.bypassSecurityTrustResourceUrl(this.sourceUrl) : undefined;
        this.imageMissing = false;
        this.emailKeyno = this.getEmailKeyno(this.file);
    }

    public onImageError(): void {
        this.imageMissing = true;
    }

    private getEmailKeyno(file?: DocumentViewerFile): string | number | undefined {
        return [file?.email_keyno, file?.oleapplication_id, file?.item_id].find((value): value is string | number => value !== undefined && value !== null && `${value}`.trim() !== '');
    }

    private getDocumentType(file?: DocumentViewerFile): DocumentViewerType {
        if (!file) return 'none';

        const contentType = file.contentType?.toLowerCase() ?? '';
        const fileName = (file.localFileName || file.fileName || '').toLowerCase();
        const extension = file.extension?.toLowerCase() || fileName.split('.').pop() || '';

        if (contentType === 'insoft.email' || extension === 'msg') return 'email';
        if (contentType.startsWith('image/') || IMAGE_EXTENSIONS.has(extension)) return 'image';
        if (contentType === 'application/pdf' || extension === 'pdf') return 'pdf';
        if (OFFICE_CONTENT_TYPES.has(contentType) || OFFICE_EXTENSIONS.has(extension)) return file.fileItemId ? 'office' : 'none';

        return 'none';
    }
}

export interface DocumentViewerFile {
    item_id?: string | number | null;
    email_keyno?: string | number | null;
    oleapplication_id?: string | number | null;
    fileItemId?: string | null;
    fileName?: string | null;
    localFileName?: string | null;
    contentType?: string | null;
    imageLink?: string | null;
    extension?: string | null;
}

export type DocumentViewerType = 'email' | 'image' | 'pdf' | 'office' | 'none';

const IMAGE_EXTENSIONS = new Set(['avif', 'bmp', 'gif', 'heic', 'heif', 'ico', 'jpeg', 'jpg', 'png', 'svg', 'tif', 'tiff', 'webp']);

const OFFICE_EXTENSIONS = new Set(['csv', 'doc', 'docm', 'docx', 'dot', 'dotm', 'dotx', 'odp', 'ods', 'odt', 'potm', 'potx', 'ppsm', 'ppsx', 'ppt', 'pptm', 'pptx', 'rtf', 'xls', 'xlsb', 'xlsm', 'xlsx']);

const OFFICE_CONTENT_TYPES = new Set([
    'text/csv',
    'application/msword',
    'application/rtf',
    'application/vnd.ms-excel',
    'application/vnd.ms-excel.sheet.binary.macroenabled.12',
    'application/vnd.ms-excel.sheet.macroenabled.12',
    'application/vnd.ms-powerpoint',
    'application/vnd.ms-powerpoint.presentation.macroenabled.12',
    'application/vnd.ms-powerpoint.slideshow.macroenabled.12',
    'application/vnd.ms-powerpoint.template.macroenabled.12',
    'application/vnd.ms-word.document.macroenabled.12',
    'application/vnd.ms-word.template.macroenabled.12',
    'application/vnd.oasis.opendocument.presentation',
    'application/vnd.oasis.opendocument.spreadsheet',
    'application/vnd.oasis.opendocument.text',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'application/vnd.openxmlformats-officedocument.presentationml.slideshow',
    'application/vnd.openxmlformats-officedocument.presentationml.template',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.template',
]);
